import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  BarChart3, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  ArrowRight, 
  PieChart, 
  Target, 
  Users,
  Calendar
} from 'lucide-react';
import { Link } from 'react-router-dom';

const JuliaARCollectionsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Julia, AR & Collections Agent</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet Julia, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> AI AR & Collections Agent</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Julia monitors your Accounts Receivable 24/7. She segments outstanding balances by age and payer type, then runs targeted follow-up campaigns to resolve them.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="w-full max-w-lg bg-white rounded-[2rem] shadow-2xl border border-slate-200 p-8 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                    
                    <div className="mb-8 flex justify-between items-center">
                        <div>
                            <h3 className="text-2xl font-bold text-slate-800">AR Dashboard</h3>
                            <p className="text-slate-500 text-sm">Real-time financial health</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>

                    {/* Aging Buckets Visualization */}
                    <div className="grid grid-cols-3 gap-4 mb-8">
                        <div className="bg-green-50 p-4 rounded-xl border border-green-100 text-center">
                            <div className="text-xs text-slate-500 mb-1">0-30 Days</div>
                            <div className="text-xl font-bold text-green-600">$42k</div>
                        </div>
                        <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-100 text-center">
                            <div className="text-xs text-slate-500 mb-1">31-60 Days</div>
                            <div className="text-xl font-bold text-yellow-600">$12k</div>
                        </div>
                        <div className="bg-red-50 p-4 rounded-xl border border-red-100 text-center">
                            <div className="text-xs text-slate-500 mb-1">90+ Days</div>
                            <div className="text-xl font-bold text-red-600">$8k</div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="text-sm font-bold text-slate-700 uppercase tracking-wide">Active Campaigns</div>
                        
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Target className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                    <div className="font-bold text-slate-700 text-sm">Insurance Follow-up</div>
                                    <div className="text-xs text-slate-500">14 claims > 45 days</div>
                                </div>
                            </div>
                            <div className="text-xs font-bold bg-green-100 text-green-700 px-2 py-1 rounded">Running</div>
                        </div>

                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <Users className="w-4 h-4 text-purple-600" />
                                </div>
                                <div>
                                    <div className="font-bold text-slate-700 text-sm">Patient Collections</div>
                                    <div className="text-xs text-slate-500">22 accounts past due</div>
                                </div>
                            </div>
                            <div className="text-xs font-bold bg-green-100 text-green-700 px-2 py-1 rounded">Running</div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Total Financial Oversight
            </h2>
            <p className="text-lg text-gray-600">
              Julia gives you complete visibility into your practice's financial health while actively working to improve it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <PieChart className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Aging Reports</h3>
              <p className="text-gray-600">
                Visualize your AR by age, payer, and type. Identify trends and bottlenecks instantly.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <Target className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Automated Follow-up</h3>
              <p className="text-gray-600">
                Julia initiates calls and emails to insurance companies for aged claims, asking for status updates automatically.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cash Flow Prediction</h3>
              <p className="text-gray-600">
                Based on historical payment data, Julia forecasts incoming revenue so you can plan expenses with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <TrendingUp className="w-4 h-4" />
                The AR Process
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How Julia Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                She works in the background, constantly monitoring your accounts and taking action on aged balances.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Monitors Ledger</h4>
                    <p className="text-slate-400 text-sm">Scans all open balances daily to update aging buckets.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Prioritizes Accounts</h4>
                    <p className="text-slate-400 text-sm">Focuses on high-value, high-age accounts first.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Executes Outreach</h4>
                    <p className="text-slate-400 text-sm">Contacts payers or patients based on the balance type.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Tracks Results</h4>
                    <p className="text-slate-400 text-sm">Logs every interaction and updates expected payment dates.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-600/20 flex items-center justify-center">
                              <Calendar className="w-5 h-5 text-indigo-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Daily Summary</div>
                              <div className="text-xs text-slate-400">Activity Log</div>
                          </div>
                      </div>
                      <div className="text-xs font-mono text-slate-500">Today</div>
                  </div>

                  <div className="space-y-4">
                      <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                          <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-bold text-white">Insurance Calls</span>
                              <span className="text-xs text-green-400 font-bold">Completed</span>
                          </div>
                          <p className="text-xs text-slate-400">
                              Contacted 12 payers regarding 18 claims. Identified 3 lost checks for reissue.
                          </p>
                      </div>

                      <div className="bg-slate-700/50 p-4 rounded-xl border border-slate-600">
                          <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-bold text-white">Patient Notices</span>
                              <span className="text-xs text-green-400 font-bold">Sent</span>
                          </div>
                          <p className="text-xs text-slate-400">
                              Sent 45 SMS reminders for balances > 60 days. Collected $1,250 via text-to-pay links.
                          </p>
                      </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">35%</div>
              <div className="text-gray-600 font-medium">Reduction in AR > 90</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">20 hrs</div>
              <div className="text-gray-600 font-medium">Saved Monthly</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">15%</div>
              <div className="text-gray-600 font-medium">Cash Flow Increase</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Take control of your AR.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Let Julia work your accounts receivable so you can focus on growing your practice.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Automate Collections
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JuliaARCollectionsPage;